import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper, Typography, Box, Divider, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableRow, TableHead,
  IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem, Switch,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
  TextField,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  getWarehouseName, getCountryName, RULES_PER_PAGE, normalizeOptionOverride,
} from './shared/helpers';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(1),
    boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
    backgroundColor: 'white',
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  sectionIcon: {
    color: '#3f51b5',
    fontSize: 24,
    marginRight: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 400,
    color: '#333',
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'flex-end',
  },
  filterControl: {
    minWidth: 180,
  },
  refreshButton: {
    backgroundColor: '#e0e0e0',
    color: '#666',
    '&:hover': { backgroundColor: '#d5d5d5' },
  },
  tableContainer: {
    boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
    borderRadius: theme.spacing(1),
  },
  tableHead: {
    backgroundColor: '#fafafa',
  },
  tableHeadCell: {
    fontWeight: 600,
    color: '#333',
    fontSize: '13px',
    borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  tableRow: {
    '&:hover': { backgroundColor: '#f5f5f5' },
  },
  tableCell: {
    padding: theme.spacing(1.5, 2),
    fontSize: '13px',
    color: '#333',
    maxWidth: 200,
  },
  optionIdCount: {
    fontFamily: 'monospace',
    fontSize: '13px',
    color: '#666',
    backgroundColor: '#f0f0f0',
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
  },
  slaBadge: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    fontWeight: 600,
    fontSize: '13px',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 12,
    display: 'inline-block',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: '#999',
  },
  emptyIcon: {
    fontSize: 48,
    color: '#ddd',
    marginBottom: theme.spacing(1),
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  loadingOverlay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
  },
}));

const OptionRulesTable = ({
  rules,
  loading,
  warehouses,
  countries: countryList,
  statusFilter,
  warehouseFilter,
  optionIdFilter,
  destinationCountryFilter,
  onFiltersChange,
  onDelete,
  onRefresh,
  onToggleStatus,
}) => {
  const classes = useStyles();
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, warehouseFilter, optionIdFilter, destinationCountryFilter]);

  const pagedRules = rules.slice((page - 1) * RULES_PER_PAGE, page * RULES_PER_PAGE);

  const confirmDelete = () => {
    if (deleteTarget) {
      onDelete(deleteTarget);
      setDeleteTarget(null);
    }
  };

  return (
    <Paper className={classes.card}>
      <Box className={classes.sectionHeader}>
        <Box className={classes.sectionHeaderLeft}>
          <LocalShippingIcon className={classes.sectionIcon} />
          <Typography className={classes.sectionTitle}>Option overrides ({rules.length})</Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton className={classes.refreshButton} onClick={onRefresh} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={classes.filtersRow}>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => onFiltersChange({ statusFilter: e.target.value })}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Warehouse</InputLabel>
          <Select
            label="Warehouse"
            value={warehouseFilter}
            onChange={(e) => onFiltersChange({ warehouseFilter: e.target.value })}
          >
            <MenuItem value=""><em>All warehouses</em></MenuItem>
            {warehouses.map((wh) => (
              <MenuItem key={wh.warehouse_id} value={wh.warehouse_id}>
                {wh.warehouse_name || wh.warehouse_id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          className={classes.filterControl}
          variant="outlined"
          size="small"
          label="Option ID"
          placeholder="Filter by option"
          value={optionIdFilter}
          onChange={(e) => onFiltersChange({ optionIdFilter: e.target.value })}
        />
        <TextField
          className={classes.filterControl}
          variant="outlined"
          size="small"
          label="Destination country"
          placeholder="e.g. AE"
          value={destinationCountryFilter}
          onChange={(e) => onFiltersChange({ destinationCountryFilter: e.target.value })}
        />
      </Box>

      <Divider style={{ marginBottom: 16 }} />

      {loading ? (
        <Box className={classes.loadingOverlay}><CircularProgress /></Box>
      ) : rules.length === 0 ? (
        <Box className={classes.emptyState}>
          <LocalShippingIcon className={classes.emptyIcon} />
          <Typography variant="body1" color="textSecondary">No option overrides match the current filters</Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>#</TableCell>
                  <TableCell className={classes.tableHeadCell}>Warehouse</TableCell>
                  <TableCell className={classes.tableHeadCell}>Option ID</TableCell>
                  <TableCell className={classes.tableHeadCell}>Country</TableCell>
                  <TableCell className={classes.tableHeadCell}>Add. SLA</TableCell>
                  <TableCell className={classes.tableHeadCell}>Active</TableCell>
                  <TableCell className={classes.tableHeadCell} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRules.map((raw, idx) => {
                  const o = normalizeOptionOverride(raw);
                  const displayOptionId = o.option_id || (o.option_ids && o.option_ids[0]) || '—';
                  const displayCountry = o.destination_country || (o.destination_countries && o.destination_countries[0]);
                  const countryLabel = displayCountry
                    ? getCountryName(displayCountry, countryList) || displayCountry
                    : '—';
                  return (
                    <TableRow key={o._id || idx} className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>{(page - 1) * RULES_PER_PAGE + idx + 1}</TableCell>
                      <TableCell className={classes.tableCell}>{getWarehouseName(o.last_mile_warehouse, warehouses)}</TableCell>
                      <TableCell className={classes.tableCell}>
                        <Tooltip title={String(displayOptionId)}>
                          <span className={classes.optionIdCount}>{displayOptionId}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={classes.tableCell}>{countryLabel}</TableCell>
                      <TableCell className={classes.tableCell}>
                        <span className={classes.slaBadge}>+{o.additional_sla}d</span>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Switch
                          size="small"
                          checked={o.status}
                          onChange={(e) => onToggleStatus(o._id, e.target.checked)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell} align="center">
                        <Tooltip title="Delete override">
                          <IconButton size="small" onClick={() => setDeleteTarget(o._id)} style={{ color: '#f44336' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {rules.length > RULES_PER_PAGE && (
            <Box className={classes.paginationContainer}>
              <Pagination
                count={Math.ceil(rules.length / RULES_PER_PAGE)}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete option override?</DialogTitle>
        <DialogContent>
          <DialogContentText>This cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} color="default">Cancel</Button>
          <Button onClick={confirmDelete} color="secondary" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default OptionRulesTable;
